// ============================================================================
// Request/response validation schemas (zod). Shared so the API enforces and the
// web app can validate against the SAME rules. Invariants here mirror the
// prototype (stop > start, no future entries, task required to stop, etc.).
// ============================================================================
import { z } from 'zod';

export const roleSchema = z.enum(['Admin', 'Manager', 'Member']);
export const timeOffTypeSchema = z.enum(['Full day off', 'Working less']);
export const epochMs = z.number().int().nonnegative();

// ── entries / timer ──────────────────────────────────────────────────────────
export const startTimerSchema = z.object({
  projectId: z.string().nullish(),
  taskId: z.string().nullish(),
  description: z.string().max(2000).optional().default(''),
});
export type StartTimerInput = z.infer<typeof startTimerSchema>;

export const updateEntrySchema = z
  .object({
    meta: z
      .object({
        description: z.string().max(2000).optional(),
        projectId: z.string().nullish(),
        taskId: z.string().nullish(),
      })
      .optional(),
    times: z
      .object({
        start: epochMs.optional(),
        stop: epochMs.optional(),
      })
      .optional(),
  })
  .refine((v) => v.meta || v.times, { message: 'Nothing to update.' });
export type UpdateEntryInput = z.infer<typeof updateEntrySchema>;

export const manualEntrySchema = z
  .object({
    ownerId: z.string().min(1, 'Pick a member.'),
    projectId: z.string().nullish(),
    taskId: z.string().min(1, 'Task is mandatory.'),
    description: z.string().trim().min(1, 'Description is mandatory.').max(2000),
    start: epochMs,
    stop: epochMs,
  })
  .refine((v) => v.stop > v.start, { message: 'Stop time must be after start time.', path: ['stop'] });
export type ManualEntryInput = z.infer<typeof manualEntrySchema>;

// ── favorites (pinned project / task in the picker) ───────────────────────────
// Exactly one of projectId / taskId identifies what to (un)favorite; `on` is the
// desired state so the endpoint is idempotent.
export const toggleFavoriteSchema = z
  .object({
    projectId: z.string().min(1).nullish(),
    taskId: z.string().min(1).nullish(),
    on: z.boolean(),
  })
  .refine((v) => (v.projectId == null) !== (v.taskId == null), {
    message: 'Provide exactly one of projectId or taskId.',
  });
export type ToggleFavoriteInput = z.infer<typeof toggleFavoriteSchema>;

// ── time off ──────────────────────────────────────────────────────────────────
export const submitTimeOffSchema = z
  .object({
    type: timeOffTypeSchema,
    mode: z.enum(['single', 'range']),
    start: epochMs,
    end: epochMs.optional(),
    reason: z.string().max(2000).optional().default(''),
  })
  .refine((v) => v.mode === 'single' || (v.end != null && v.end >= v.start), {
    message: "End date can't be before start date.",
    path: ['end'],
  });
export type SubmitTimeOffInput = z.infer<typeof submitTimeOffSchema>;

export const decideTimeOffSchema = z.object({ status: z.enum(['Approved', 'Rejected']) });

// ── manage: teams / projects / tasks / users ─────────────────────────────────
export const createTeamSchema = z.object({ name: z.string().trim().min(1).max(120) });
export const renameTeamSchema = createTeamSchema;

export const createProjectSchema = z.object({
  name: z.string().trim().min(1).max(160),
  teamIds: z.array(z.string()).default([]),
});
export const updateProjectSchema = createProjectSchema;

export const createTaskSchema = z.object({
  projectId: z.string().min(1),
  name: z.string().trim().min(1).max(160),
  teamIds: z.array(z.string()).default([]),
});
export const updateTaskSchema = z.object({
  name: z.string().trim().min(1).max(160),
  teamIds: z.array(z.string()).default([]),
});

export const setRoleSchema = z.object({ role: roleSchema });
export const updateUserSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(120),
  email: z.string().trim().toLowerCase().email('Enter a valid email address.'),
});
export const inviteSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: roleSchema.default('Member'),
});

// ── reports / dashboard query ─────────────────────────────────────────────────
// Filters arrive as comma-separated id lists (multi-select); empty/absent = all.
const idList = z
  .string()
  .optional()
  .transform((s) => {
    const list = s ? s.split(',').filter(Boolean) : [];
    return list.length ? list : undefined;
  });

export const reportFilterSchema = z.object({
  from: z.coerce.number().int().nonnegative(),
  to: z.coerce.number().int().nonnegative(),
  member: idList,
  team: idList,
  project: idList,
  task: idList,
  search: z.string().optional(),
});
export type ReportFilter = z.infer<typeof reportFilterSchema>;

export const summaryQuerySchema = reportFilterSchema.extend({
  groupBy: z
    .string()
    .optional()
    .transform((s) => (s ? s.split(',').filter(Boolean) : ['User', 'Project', 'Description'])),
});

export const dashboardQuerySchema = z.object({
  from: z.coerce.number().int().nonnegative(),
  to: z.coerce.number().int().nonnegative(),
  onlyMe: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === 'true' || v === '1'),
});
